import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
    Chat,
    ChatEntity,
    ChatHistory,
    ChatHistoryByDate,
    ChatMap,
    ChatTimelineThinkingMode,
    ContextInfo,
    Message,
    ResearchLink,
    Subquestion,
    Tag
} from "../../components/GlobalChatBox/types";
import { MentionSearchType } from "../../components/GlobalChatBox/types/mentionSearch";
import { DEFAULT_MENTION_SEARCH_CACHE } from '../../constants/constants';

const getLocalStorageItem = (key: string, defaultValue: any) => {
    try {
        const item = localStorage.getItem(key);
        return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
        return defaultValue;
    }
};

interface MentionSearchCache {
    items: any[];
    searchTerm: string;
    page: number;
    totalCount: number;
    hasMore: boolean;
}

interface ChatMessageState {
    messages: any[],
    loading: boolean,
    error: string | null,
    chatSubquestions: Subquestion[],
    chatSubquestionsLoading: boolean,
    chatSubquestionsError: string | null,
    lastActiveQuestionsLoading: boolean,
    chatLastActiveQuestionsError: string | null,
    lastActiveQuestions: any[],
    isChatModalOpen: boolean,
    chatHistoryListLoading: boolean,
    setNewChatIdLoading: boolean,
    setNewChatId: string,

    lastActiveChatId: string | number | null,
    chatIdForDelete: number | null;
    projectId: string;
    chatHistoryByDate: ChatHistoryByDate,
    chats: ChatMap,
    pinnedChats: ChatHistory[],
    pinnedChatsLoading?: boolean,
    pinnedChatsError?: any,
    chatShareData?: any,
    chatShareLoading?: boolean,
    chatShareError?: any,
    sessionId: string | null,
    sessionExpired: boolean,
    isCreatingSession: boolean,
    isNewChat: boolean,
    feedbackLoading?: { [messageId: string]: boolean },
    feedbackError?: { [messageId: string]: string | null },
    feedbackStatus?: { [messageId: string]: boolean | null },
    // Interrupt-related state (removed activeInterrupts in favor of message-level tag)
    interruptLoading: boolean,
    currentPage: number,
    hasMoreChats: boolean,
    isLoadingMore: boolean,
    searchQuery: string,
    totalChats: number,

    // global chatbot states
    showChatbox: boolean,
    showBigChatbox: boolean,
    showNormalChatbox: boolean,
    isChatbotSidebarCollapsed: boolean,
    showHistoryDropdown: boolean,
    showSearchInterface: boolean,
    showSourcesPanel: boolean,
    researchLinks: ResearchLink[],
    contextInfo: ContextInfo,
    chatEntity: ChatEntity,
    thinkingMode: ChatTimelineThinkingMode,

    mentionSearchCache: {
        all: MentionSearchCache;
        project: MentionSearchCache;
        strategymap: MentionSearchCache;
        team: MentionSearchCache;
        resource: MentionSearchCache;
        roadmap: MentionSearchCache;
        epic: MentionSearchCache;
        feature: MentionSearchCache;
    };
    mentionSearchLoading: boolean;
    mentionLoadingMore: boolean;
    profileId: number | null;
    profileLoading: boolean;
    profileError: string | null;
    isSharedChat: boolean;
}

const initialState: ChatMessageState = {
    messages: [],
    loading: false,
    error: null,
    chatSubquestions: [],
    chatSubquestionsLoading: false,
    chatSubquestionsError: null,
    lastActiveQuestionsLoading: false,
    chatLastActiveQuestionsError: null,
    lastActiveQuestions: [],
    isChatModalOpen: false,
    chatHistoryListLoading: false,
    setNewChatIdLoading: false,
    setNewChatId: '',

    lastActiveChatId: null,
    chatIdForDelete: null,
    projectId: '',
    chatHistoryByDate: {
        older: [],
        today: [],
        yesterday: [],
    },
    chats: {},
    pinnedChats: [],
    pinnedChatsLoading: false,
    pinnedChatsError: null,
    chatShareData: null,
    chatShareLoading: false,
    chatShareError: null,
    sessionId: null,
    sessionExpired: false,
    isCreatingSession: false,
    isNewChat: true,
    feedbackLoading: {},
    feedbackError: {},
    feedbackStatus: {},
    interruptLoading: false,
    currentPage: 1,
    hasMoreChats: true,
    isLoadingMore: false,
    searchQuery: '',
    totalChats: 0,

    showChatbox: false,
    showBigChatbox: false,
    showNormalChatbox: false,
    isChatbotSidebarCollapsed: false,
    showHistoryDropdown: false,
    showSearchInterface: false,
    showSourcesPanel: false,
    researchLinks: [],
    contextInfo: {
        pageName: "",
        chatButtonText: "Ask Cadence",
        headerTitle: "Chat with CadenceAI",
        welcomeMessage: "",
        contextPayload: {
            context_type: "",
            project_id: "",
            entity: "",
            entity_id: "",
            page: ""
        },
        entity_name: ""
    },
    chatEntity: {
        entity: "",
        entityId: null
    },

    mentionSearchCache:DEFAULT_MENTION_SEARCH_CACHE,
    mentionSearchLoading: false,
    mentionLoadingMore: false,
    thinkingMode: getLocalStorageItem("cadence-chat-thinking-mode", ChatTimelineThinkingMode.COMPACT),
    profileId: null,
    profileLoading: false,
    profileError: null,
    isSharedChat: false,
}

export const chatMessageSlice = createSlice({
    name: 'chatMessage',
    initialState: initialState,
    reducers: {
        setChatMessages: (state: any, action) => {
            state.messages = [...state.messages, { text: action?.payload }]
        },
        setChatMessagesLoading: (state: any, action) => {
            state.loading = action.payload
        },
        setChatMessagesError: (state: any, action) => {
            state.error = action.payload
        },
        setChatIdForDelete: (state: any, action: PayloadAction<number>) => {
            state.chatIdForDelete = action.payload
        },
        resetChatIdForDelete: (state: any) => {
            state.chatIdForDelete = null
        },
        setProjectId: (state: any, action: PayloadAction<string>) => {
            state.projectId = action.payload
        },
        resetProjectId: (state: any) => {
            state.projectId = ''
        },
        setChatSubquestions: (state: any, action: PayloadAction<Subquestion[]>) => {
            state.chatSubquestions = action?.payload
        },
        setChatSubquestionsLoading: (state: any, action) => {
            state.chatSubquestionsLoading = action.payload
        },
        setChatSubquestionsError: (state: any, action) => {
            state.chatSubquestionsError = action.payload
        },
        setLastActiveChatId: (state: any, action) => {
            state.lastActiveChatId = action?.payload
        },
        setLastActiveQuestions: (state: any, action) => {
            state.lastActiveQuestions = action?.payload
        },
        setChatLastActiveQuestionsLoading: (state: any, action) => {
            state.lastActiveQuestionsLoading = action.payload
        },
        setChatLastActiveQuestionsError: (state: any, action) => {
            state.chatLastActiveQuestionsError = action.payload
        },

        setThinkingMode: (state: any, action: PayloadAction<ChatTimelineThinkingMode>) => {
            state.thinkingMode = action.payload
        },
        setShowSearchInterface: (state: any, action: PayloadAction<boolean>) => {
            state.showSearchInterface = action.payload
        },
        setShowHistoryDropdown: (state: any, action: PayloadAction<boolean>) => {
            state.showHistoryDropdown = action.payload
        },
        setIsChatbotSidebarCollapsed: (state: any, action: PayloadAction<boolean>) => {
            state.isChatbotSidebarCollapsed = action.payload
        },
        setShowChatbox: (state: any, action: PayloadAction<boolean>) => {
            state.showChatbox = action.payload
        },
        setShowBigChatbox: (state: any, action: PayloadAction<boolean>) => {
            state.showBigChatbox = action.payload
        },
        setShowNormalChatbox: (state: any, action: PayloadAction<boolean>) => {
            state.showNormalChatbox = action.payload
        },
        setShowSourcesPanel: (state: any, action: PayloadAction<boolean>) => {
            state.showSourcesPanel = action.payload
        },
        setResearchLinks: (state: any, action: PayloadAction<ResearchLink[]>) => {
            state.researchLinks = action.payload
        },
        setContextInfo: (state: any, action: PayloadAction<ContextInfo>) => {
            state.contextInfo = action.payload
        },
        setChatEntity: (state: any, action: PayloadAction<ChatEntity>) => {
            state.chatEntity = action.payload
        },

        setBigChatboxToggle: (state: any, action) => {
            state.isChatModalOpen = action.payload
            state.lastActiveQuestions = []
        },
        setIsNewChat: (state: any, action: PayloadAction<boolean>) => {
            state.isNewChat = action.payload

            if (action.payload) {
                const newChat: Chat = {
                    chatId: "new-chat",
                    title: "",
                    projectId: "",
                    createdAt: new Date().toISOString(),
                    createdBy: "",

                    messages: [],
                    isStreaming: false,
                    isLoading: false,
                }

                state.chats["new-chat"] = newChat;
            } else {
                console.log("setIsNewChat else")
            }
        },
        setChatHistoryListLoading: (state: any, action) => {
            state.chatHistoryListLoading = action.payload
        },
        setChatHistoryListError: (state: any, action) => {
            state.chatHistoryListError = action.payload
        },
        setChatHistoryList: (state: any, action: PayloadAction<ChatHistoryByDate>) => {
            state.chatHistoryByDate = action.payload || { today: [], yesterday: [], older: [] };
            state.currentPage = 1;
        },
        appendChatHistoryList: (state: any, action: PayloadAction<ChatHistoryByDate>) => {
            const newData = action.payload;

            if (!state.chatHistoryByDate) {
                state.chatHistoryByDate = { today: [], yesterday: [], older: [] };
            }

            const existingIds = new Set([
                ...(state.chatHistoryByDate.today || []).map((c: any) => c.chat_id),
                ...(state.chatHistoryByDate.yesterday || []).map((c: any) => c.chat_id),
                ...(state.chatHistoryByDate.older || []).map((c: any) => c.chat_id)
            ]);

            const newToday = (newData.today || []).filter((c: any) => !existingIds.has(c.chat_id));
            const newYesterday = (newData.yesterday || []).filter((c: any) => !existingIds.has(c.chat_id));
            const newOlder = (newData.older || []).filter((c: any) => !existingIds.has(c.chat_id));

            state.chatHistoryByDate.today = [...(state.chatHistoryByDate.today || []), ...newToday];
            state.chatHistoryByDate.yesterday = [...(state.chatHistoryByDate.yesterday || []), ...newYesterday];
            state.chatHistoryByDate.older = [...(state.chatHistoryByDate.older || []), ...newOlder];
        },
        setChatMessageByIdLoading: (state: any, action) => {
            state.chatMessageByIdLoading = action.payload
        },
        setChatMessageByIdError: (state: any, action) => {
            state.chatMessageByIdError = action.payload
        },
        setChatMessageById: (state: any, action) => {
            state.chatMessageById = action.payload
        },
        setPinnedChatsLoading: (state: any, action) => {
            state.pinnedChatsLoading = action.payload
        },
        setPinnedChatsError: (state: any, action) => {
            state.pinnedChatsError = action.payload
        },
        setPinnedChats: (state: any, action: PayloadAction<ChatHistory[]>) => {
            state.pinnedChats = action.payload || []
        },
        setChatShareLoading: (state: any, action) => {
            state.chatShareLoading = action.payload
        },
        setChatShareError: (state: any, action) => {
            state.chatShareError = action.payload
        },
        setChatShare: (state: any, action) => {
            state.chatShareData = action.payload
        },
        setNewChatIdLoading: (state: any, action) => {
            state.newChatIdLoading = action.payload
        },
        setSessionExpiry: (state: any, action: PayloadAction<boolean | null>) => {
            state.sessionExpired = action.payload
        },
        setSessionId: (state: any, action: PayloadAction<string | null>) => {
            state.sessionId = action.payload
        },
        setIsCreatingSession: (state: any, action: PayloadAction<boolean>) => {
            state.isCreatingSession = action.payload
        },
        setFetchedChatWithMessages: (state, action: PayloadAction<{
            chatId: number,
            messages: any[] // assuming raw API messages
        }>) => {
            const { chatId, messages } = action.payload;
            const chatIdStr = String(chatId);

            const parsedMessages: Message[] = messages.map((m: any) => {
                const tags: Tag[] = [];

                if (m.thinking_tokens && Array.isArray(m.thinking_tokens)) {
                    m.thinking_tokens.forEach((tokenObj: any) => {
                        const { type, token } = tokenObj;

                        const tag: Tag = {
                            type,
                            content: '',
                            text: '',
                            progressId: null,
                            isStreaming: false,
                            isComplete: true
                        };

                        try {
                            const match = token.match(new RegExp(`<${type}>([\\s\\S]*?)</${type}>`));
                            if (match && match[1]) {
                                const inner = match[1].trim();

                                const shortMessageMatch = inner.match(/["']short_message["']\s*:\s*["']([\s\S]*?)["']/);
                                if (type === 'research' && shortMessageMatch) {
                                    tag.text = shortMessageMatch[1];
                                }

                                if (type === 'thinking') {
                                    const boldMatch = inner.match(/\*\*([\s\S]*?)\*\*/);
                                    if (boldMatch) {
                                        tag.text = boldMatch[1].trim();
                                    }
                                }

                                const idMatch = inner.match(/["']id["']\s*:\s*["']([^"']+)["']/);
                                if (idMatch) {
                                    tag.progressId = idMatch[1];
                                }
                            }

                            tag.content = token;
                        } catch (e) {
                            tag.content = token;
                        }

                        tags.push(tag);
                    });
                }

                // Normalize backend interrupt array into Message.interrupts
                const interrupts = Array.isArray(m.interrupt)
                    ? m.interrupt.map((it: any) => ({ type: it.type, data: it }))
                    : [];

                // If there are stored memory_saved entries in interrupt JSON, reflect them as tags
                const memorySavedInterrupts = Array.isArray(m.interrupt)
                    ? m.interrupt.filter((it: any) => it?.type === 'memory_saved')
                    : [];
                if (memorySavedInterrupts.length > 0 && m.is_bot) {
                    memorySavedInterrupts.forEach((memorySavedInterrupt: any) => {
                        const memoryText = memorySavedInterrupt.token || memorySavedInterrupt.user_friendly_text || '';
                        if (memoryText && String(memoryText).trim().length > 0) {
                            tags.push({
                                type: 'memory_saved',
                                content: memoryText,
                                text: memoryText,
                                progressId: null,
                                isComplete: true,
                                isStreaming: false,
                            });
                        }
                    });
                }

                // If there is a question_ui interrupt, append its question to message text
                const questionInt = interrupts.find((i: any) => i?.type === 'question_ui');
                const baseText = m.text || '';
                const textWithInterruptAnswer = (questionInt && questionInt.data?.question)
                    ? (baseText + questionInt.data.question)
                    : baseText;

                return {
                    id: m.is_bot ? `bot-${m.message_id}` : `user-${m.message_id}`,
                    messageId: m.message_id,
                    text: textWithInterruptAnswer,
                    isBot: m.is_bot || false,
                    isEdited: false,
                    tags,
                    isTimelineExpanded: false,
                    modified_at: m.modified_at,
                    created_at: m.created_at,
                    queued_at: m.queued_at,
                    interrupts
                } as Message;
            });

            if (state.chats[chatIdStr]) {
                state.chats[chatIdStr].messages = parsedMessages;
            } else {
                state.chats[chatIdStr] = {
                    chatId,
                    title: "",
                    projectId: "",
                    createdAt: new Date().toISOString(),
                    createdBy: "",
                    messages: parsedMessages,
                    isStreaming: false,
                    isLoading: false
                };
            }
        },
        populateChatsFromHistory: (state, action: PayloadAction<ChatHistoryByDate>) => {
            const allChats = [
                ...action.payload.today,
                ...action.payload.yesterday,
                ...action.payload.older
            ];

            allChats.forEach(chat => {
                const chatIdStr = String(chat.chat_id);
                if (!state.chats[chatIdStr]) {
                    state.chats[chatIdStr] = {
                        chatId: chat.chat_id,
                        title: chat.title,
                        projectId: chat.project_id,
                        createdAt: chat.created_at,
                        createdBy: chat.created_by,
                        messages: [],
                        isStreaming: false,
                        isLoading: false
                    };
                } else {
                    state.chats[chatIdStr] = {
                        ...state.chats[chatIdStr],
                        title: chat.title,
                        projectId: chat.project_id,
                        createdAt: chat.created_at,
                        createdBy: chat.created_by,
                    };
                }
            });

        },
        updateThinkingTag: (state: any, action: PayloadAction<{
            chatId: string,
            token: string
        }>) => {
            const { chatId, token } = action.payload;

            const currentChat: Chat = state.chats[chatId]
            const messages: Message[] = [...currentChat.messages]
            const lastIndex = messages.length - 1;

            if (lastIndex < 0 || !messages[lastIndex].isBot) return;

            const message = { ...messages[lastIndex] };
            const existingTags = [...message.tags];

            const completeTagMatch = token && token.match(/<thinking>([\s\S]+?)<\/thinking>/);
            if (completeTagMatch) {
                const content = completeTagMatch[1].trim();

                // Add as a complete tag
                const tagData: Tag = {
                    type: 'thinking',
                    content: content,
                    text: (() => {
                        const bold = content.match(/\*\*([\s\S]*?)\*\*/);
                        return bold ? bold[1].trim() : "";
                    })(),
                    progressId: null,
                    isComplete: true,
                    isStreaming: false
                };

                existingTags.push(tagData);
            }
            else if (token === `<thinking>`) {
                const tagData: Tag = {
                    type: 'thinking',
                    content: '',
                    text: "",
                    progressId: null,
                    isStreaming: true,
                    isComplete: false
                };
                existingTags.push(tagData);
            }
            else if (token === `</thinking>`) {
                const lastStreamingTag = existingTags
                    .slice()
                    .reverse()
                    .find(tag => tag.type === 'thinking' && tag.isStreaming);

                if (lastStreamingTag) {
                    lastStreamingTag.isStreaming = false;
                    lastStreamingTag.isComplete = true;
                    if (!lastStreamingTag.text && lastStreamingTag.content) {
                        const bold = lastStreamingTag.content.match(/\*\*([\s\S]*?)\*\*/);
                        lastStreamingTag.text = bold ? bold[1].trim() : '';
                    }
                }
            } else {
                const lastStreamingTag = existingTags
                    .slice()
                    .reverse()
                    .find(tag => tag.type === 'thinking' && tag.isStreaming);

                if (lastStreamingTag) {
                    lastStreamingTag.content += token;
                }
            }

            message.tags = existingTags;
            messages[lastIndex] = message;
            currentChat.isLoading = false;
            currentChat.messages = messages;
        },
        updateMessageCancelledTag: (state: any, action: PayloadAction<{
            token: string;
        }>) => {
            const { token } = action.payload;
            const chatId = state.lastActiveChatId || "new-chat";

            const currentChat: Chat = state.chats[chatId]
            const messages: Message[] = [...currentChat.messages]
            const lastIndex = messages.length - 1;
            if (lastIndex < 0 || !messages[lastIndex].isBot) return;

            const message = { ...messages[lastIndex] };
            message.text = token;
            messages[lastIndex] = message;
            currentChat.isLoading = false;
            currentChat.isStreaming = false;
            currentChat.messages = messages;
        },
        updateMessageCompleteTag: (state: any, action: PayloadAction<{
            chatId: string;
        }>) => {
            const { chatId } = action.payload;
            const currentChat: Chat = state.chats[chatId]

            currentChat.isLoading = false;
            currentChat.isStreaming = false;
        },
        updateFinalTag: (state: any, action: PayloadAction<{
            chatId: string,
            token: string
        }>) => {
            const { chatId, token } = action.payload;

            const currentChat: Chat = state.chats[chatId]
            const messages: Message[] = [...currentChat.messages]
            const lastIndex = messages.length - 1;
            const message = { ...messages[lastIndex] };

            if (lastIndex < 0 || !messages[lastIndex].isBot) return;

            message.text = token;
            messages[lastIndex] = message;
            currentChat.isLoading = false;
            currentChat.isStreaming = false;
            currentChat.messages = messages;
        },
        updateAnswerTag: (state: any, action: PayloadAction<{
            chatId: string,
            token: string
        }>) => {
            const { chatId, token } = action.payload;

            const currentChat: Chat = state.chats[chatId]
            const messages: Message[] = [...currentChat.messages]
            const lastIndex = messages.length - 1;
            const message = { ...messages[lastIndex] };
            const existingTags = [...messages[lastIndex].tags];

            if (lastIndex < 0 || !messages[lastIndex].isBot) return;

            existingTags.forEach(tag => {
                tag.isStreaming = false;
                tag.isComplete = true;
            });

            message.tags = existingTags;
            message.text = message.text + token;
            messages[lastIndex] = message;
            currentChat.isLoading = false;
            currentChat.isStreaming = true;
            currentChat.messages = messages;
        },
        updateTransferOrProgressTag: (state: any, action: PayloadAction<{
            chatId: string;
            token: string,
            type: string
        }>) => {
            const { chatId, token, type } = action.payload;

            const currentChat: Chat = state.chats[chatId]
            const messages: Message[] = [...currentChat.messages]
            const lastIndex = messages.length - 1;

            if (lastIndex < 0 || !messages[lastIndex].isBot) return;

            const message = { ...messages[lastIndex] };
            const existingTags = [...message.tags];
            let content = token;


            const tagMatch = content.match(new RegExp(`<${type}>([\\s\\S]+?)</${type}>`));
            if (tagMatch) {
                content = tagMatch[1].trim();
            }

            const tagData: Tag = {
                type: type,
                content: content,
                text: "",
                progressId: null,
                isComplete: true,
                isStreaming: false
            };

            if (type === 'progress') {
                try {
                    let progressContent = content;
                    progressContent = progressContent.replace(/'/g, '"');

                    const progressData = JSON.parse(progressContent);
                    if (progressData.id) {
                        tagData.progressId = progressData.id;
                        tagData.text = progressData.message;
                    }
                } catch (e) {
                    // If parsing fails, just continue without the ID
                }
            }
            else if (type === 'research') {
                try {
                    let researchContent = content;
                    researchContent = researchContent.replace(/'/g, '"');
                    const sm = researchContent.match(/\"short_message\"\s*:\s*\"([\s\S]*?)\"/);
                    if (sm) {
                        tagData.text = sm[1];
                    }
                } catch (e) {
                    // ignore
                }
            } else if (type === 'transfer') {
                try {
                    const clean = content.replace(/'/g, '"');
                    const obj = JSON.parse(clean);
                    if (obj?.token) tagData.text = obj.token;
                } catch (e) {
                    const m = content.match(/'token':\s*'([^']+)'/);
                    if (m) tagData.text = m[1];
                }
            } else if (type === 'summarization') {
                try {
                    const clean = content.replace(/'/g, '"');
                    const obj = JSON.parse(clean);
                    if (obj?.token) tagData.text = obj.token;
                    if (obj?.stage) tagData.progressId = obj.stage; // Use progressId to store stage
                } catch (e) {
                    const m = content.match(/'token':\s*'([^']+)'/);
                    if (m) tagData.text = m[1];
                    const stageM = content.match(/'stage':\s*'([^']+)'/);
                    if (stageM) tagData.progressId = stageM[1];
                }
            }

            existingTags.push(tagData);

            message.tags = existingTags;
            messages[lastIndex] = message;
            currentChat.isLoading = false;
            currentChat.messages = messages;
        },
        addMemorySavedTag: (state: any, action: PayloadAction<{
            chatId: string;
            memoryText: string;
        }>) => {
            const { chatId, memoryText } = action.payload;

            const currentChat: Chat = state.chats[chatId];
            if (!currentChat || !Array.isArray(currentChat.messages) || currentChat.messages.length === 0) return;

            const messages: Message[] = [...currentChat.messages];

            // Find the last bot message in this chat
            let idx = messages.length - 1;
            while (idx >= 0 && !messages[idx].isBot) {
                idx--;
            }
            if (idx < 0) return;

            const message = { ...messages[idx] } as Message;
            const existingTags = Array.isArray(message.tags) ? [...message.tags] : [];

            const tagData: Tag = {
                type: 'memory_saved',
                content: memoryText,
                text: memoryText,
                progressId: null,
                isComplete: true,
                isStreaming: false,
            };

            existingTags.push(tagData);
            message.tags = existingTags;
            messages[idx] = message;
            currentChat.messages = messages;
        },
        // New: Upsert an interrupt onto a specific message (or last bot message if not found)
        upsertInterruptOnMessage: (state: any, action: PayloadAction<{
            chatId: string;
            messageId?: number | null;
            type: 'question_ui' | 'simulation_approval_ui' | 'SimulationDiff';
            data: any; // raw interrupt payload
        }>) => {
            const { chatId, messageId, type, data } = action.payload;
            const chat = state.chats?.[chatId];
            if (!chat || !Array.isArray(chat.messages) || chat.messages.length === 0) return;

            let idx = -1;
            if (messageId) {
                idx = chat.messages.findIndex((m: any) => m.messageId === messageId && m.isBot);
            }
            if (idx === -1) {
                for (let i = chat.messages.length - 1; i >= 0; i--) {
                    if (chat.messages[i].isBot) { idx = i; break; }
                }
            }
            if (idx === -1) return;

            const msg = chat.messages[idx] as Message;
            const nextInterrupts = Array.isArray(msg.interrupts) ? [...msg.interrupts] : [];
            const existingIndex = nextInterrupts.findIndex((i: any) => i.type === type);
            const newEntry = { type, data } as any;
            if (existingIndex >= 0) {
                nextInterrupts[existingIndex] = newEntry;
            } else {
                nextInterrupts.push(newEntry);
            }

            chat.messages[idx] = { ...msg, interrupts: nextInterrupts } as Message;
            chat.isStreaming = false;
            chat.isLoading = false;
        },
        clearInterruptOnMessage: (state: any, action: PayloadAction<{
            chatId: string;
            messageId?: number | null;
        }>) => {
            const { chatId, messageId } = action.payload;
            const chat = state.chats?.[chatId];
            if (!chat || !Array.isArray(chat.messages) || chat.messages.length === 0) return;

            let idx = -1;
            if (messageId) {
                idx = chat.messages.findIndex((m: any) => m.messageId === messageId && m.isBot);
            }
            if (idx === -1) {
                for (let i = chat.messages.length - 1; i >= 0; i--) {
                    if (chat.messages[i].isBot) { idx = i; break; }
                }
            }
            if (idx === -1) return;

            const target = chat.messages[idx] as Message;
            if (Array.isArray(target?.interrupts) && target.interrupts.length > 0) {
                chat.messages[idx] = {
                    ...target,
                    interrupts: target.interrupts.filter((i: any) => i.type === 'SimulationDiff')
                } as Message;
            }
        },
        setBotMessageError: (state: any, action: PayloadAction<{
            chatId: string;
            error: string;
        }>) => {
            const { chatId, error } = action.payload;

            const currentChat = state.chats[chatId]
            const messages = [...currentChat.messages]
            const lastIndex = messages.length - 1

            if (lastIndex >= 0 && messages[lastIndex].isBot) {
                messages[lastIndex] = {
                    ...messages[lastIndex],
                    text: error,
                    isError: true,
                };
            }

            currentChat.messages = messages
            currentChat.isStreaming = false
            currentChat.isLoading = false

            state.chats[chatId] = currentChat
        },
        editLastUserMessage: (state: any, action: PayloadAction<{
            chatId: string,
            messageId: number,
            text: string,
        }>) => {
            const { chatId, messageId, text } = action.payload;

            const currentChat = state.chats[chatId]
            const messages = [...currentChat.messages]
            let messageIndex = -1;

            for (let i = 0; i < messages.length; i++) {
                if (messages[i].messageId === messageId) {
                    messageIndex = i;
                    break;
                }
            }

            if (messageIndex !== -1) {
                messages[messageIndex] = {
                    ...messages[messageIndex],
                    text: text
                }

                const messagesToKeep = messages.slice(0, messageIndex + 1);

                const botMessage = {
                    text: '',
                    isBot: true,
                    tags: [],
                    id: `bot-${Date.now()}`
                };
                currentChat.isLoading = true
                currentChat.messages = [
                    ...messagesToKeep,
                    botMessage
                ]

                state.chats[chatId] = currentChat
            }
        },
        updateMessageId: (state: any, action: PayloadAction<{
            chatId: string,
            messageId: number,
            isBot: boolean
        }>) => {
            const { chatId, messageId, isBot } = action.payload;

            if (!state.chats[chatId]) {
                state.chats[chatId] = {
                    ...state.chats["new-chat"],
                    chatId,
                };
            }

            const messages = [...state.chats[chatId].messages]

            for (let i = messages.length - 1; i >= 0; i--) {
                const msg = messages[i];
                if (isBot) {
                    if (msg.isBot && !msg.messageId) {
                        msg.messageId = messageId; // <- direct mutation
                        break;
                    }
                } else {
                    if (!msg.isBot && !msg.messageId) {
                        msg.messageId = messageId; // <- direct mutation
                        break;
                    }
                }
            }
        },
        setChatToStreaming: (state: any, action: PayloadAction<{
            chatId: string;
            streaming: boolean;
        }>) => {
            const { chatId, streaming } = action.payload;

            if (state.chats[chatId]) {
                state.chats[chatId].isStreaming = streaming;
            } else {
                console.log("setChatToStreaming else")
            }
        },
        setChatToLoading: (state: any, action: PayloadAction<{
            chatId: string,
            loading: boolean
        }>) => {
            const { chatId, loading } = action.payload;

            if (state.chats[chatId]) {
                state.chats[chatId].isLoading = loading;
            } else {
                console.log("setChatToLoading else")
            }
        },
        setQueuedAtForLastMessages: (state: any, action: PayloadAction<{
            chatId: string,
            queued_at: string,
        }>) => {
            const { chatId, queued_at } = action.payload;

            const targetChatId = String(chatId);
            const currentChat: Chat = state.chats[targetChatId];
            if (!currentChat || !Array.isArray(currentChat.messages)) return;

            const messages: Message[] = currentChat.messages;

            let lastUserIndex = -1;
            let lastBotIndex = -1;
            for (let i = messages.length - 1; i >= 0; i--) {
                if (lastBotIndex === -1 && messages[i].isBot) lastBotIndex = i;
                if (lastUserIndex === -1 && !messages[i].isBot) lastUserIndex = i;
                if (lastBotIndex !== -1 && lastUserIndex !== -1) break;
            }

            if (lastUserIndex !== -1) {
                const m = messages[lastUserIndex];
                m.modified_at = queued_at;
                if (!m.queued_at) m.queued_at = queued_at;
            }
            if (lastBotIndex !== -1) {
                const m = messages[lastBotIndex];
                m.modified_at = queued_at;
                if (!m.queued_at) m.queued_at = queued_at;
            }

            currentChat.messages = [...messages];
        },
        setUserAndBotMessageToChat: (state: any, action: PayloadAction<{
            chatId: string,
            userMessage: Message,
            botMessage: Message,
        }>) => {
            const { userMessage, botMessage, chatId } = action.payload;

            // Determine final target chat ID
            const activeChatId = state.lastActiveChatId !== null ? String(state.lastActiveChatId) : chatId;

            // If "new-chat" was used but lastActiveChatId exists, migrate
            if (chatId === "new-chat" && activeChatId !== "new-chat" && state.chats["new-chat"]) {
                // Migrate the chat data
                state.chats[activeChatId] = {
                    ...state.chats["new-chat"],
                    chatId: Number(activeChatId),
                };
                delete state.chats["new-chat"];
            }

            if (state.chats[chatId]) {
                state.chats[chatId].messages = [
                    ...state.chats[chatId].messages,
                    userMessage,
                    botMessage,
                ];
            } else {
                console.log("setUserAndBotMessageToChat else")
            }
        },
        addNewChatToHistory: (state: any, action: PayloadAction<{
            chat_id: number,
            title?: string,
            project_id?: string
        }>) => {
            const { chat_id, title = "New Chat", project_id = "" } = action.payload;

            const existsInToday = state.chatHistoryByDate.today.some((chat: any) => chat.chat_id === chat_id);
            const existsInYesterday = state.chatHistoryByDate.yesterday.some((chat: any) => chat.chat_id === chat_id);
            const existsInOlder = state.chatHistoryByDate.older.some((chat: any) => chat.chat_id === chat_id);
            const existsInPinned = Array.isArray(state.pinnedChats) && state.pinnedChats.some((chat: any) => chat.chat_id === chat_id);

            if (!existsInToday && !existsInYesterday && !existsInOlder && !existsInPinned) {
                const chat = state.chats["new-chat"];
                let messages = [];
                let isLoading = false
                let isStreaming = false;

                if (chat) {
                    messages = chat.messages;
                    isLoading = chat.isLoading;
                    isStreaming = chat.isStreaming;
                }

                const newChatEntry: ChatHistory = {
                    chat_id: chat_id,
                    title,
                    project_id: project_id,
                    created_at: new Date().toISOString(),
                    created_by: "current_user",
                    updated_at: new Date().toISOString(),
                };

                state.chatHistoryByDate.today.unshift(newChatEntry);
                state.chats[chat_id] = {
                    chatId: chat_id,
                    title: title,
                    projectId: project_id,
                    createdAt: new Date().toISOString(),
                    createdBy: "current_user",

                    messages: messages,
                    isStreaming: isStreaming,
                    isLoading: isLoading
                };
            }
        },
        updateChatTitle: (state: any, action: PayloadAction<{ chat_id: number, title: string }>) => {
            const { chat_id, title } = action.payload;
            const chatIdStr = String(chat_id);

            const updateTitle = (arr: any[]) => {
                const chat = arr.find(c => c.chat_id === chat_id);
                if (chat) chat.title = title;
            };

            updateTitle(state.chatHistoryByDate.today);
            updateTitle(state.chatHistoryByDate.yesterday);
            updateTitle(state.chatHistoryByDate.older);

            if (state.chats[chatIdStr]) {
                state.chats[chatIdStr].title = title;
            }
        },
        deleteChat: (state: any, action: PayloadAction<number>) => {
            const chat_id = action.payload;
            const chatIdStr = String(chat_id);

            const deleteFromArray = (arr: any[]) => {
                const index = arr.findIndex(chat => chat.chat_id === chat_id);
                if (index !== -1) arr.splice(index, 1);
            };

            deleteFromArray(state.chatHistoryByDate.today);
            deleteFromArray(state.chatHistoryByDate.yesterday);
            deleteFromArray(state.chatHistoryByDate.older);

            delete state.chats[chatIdStr];
            if (Array.isArray(state.pinnedChats)) {
                state.pinnedChats = state.pinnedChats.filter((c: any) => c.chat_id !== chat_id);
            }
        },
        removeChatFromHistoryLists: (state: any, action: PayloadAction<number>) => {
            const chat_id = action.payload;
            const deleteFromArray = (arr: any[]) => {
                const index = arr.findIndex(chat => chat.chat_id === chat_id);
                if (index !== -1) arr.splice(index, 1);
            };

            deleteFromArray(state.chatHistoryByDate.today);
            deleteFromArray(state.chatHistoryByDate.yesterday);
            deleteFromArray(state.chatHistoryByDate.older);
        },
        setMessageFeedbackLoading: (state: any, action) => {
            const { messageId, loading } = action.payload;
            state.feedbackLoading[messageId] = loading;
        },
        setMessageFeedbackError: (state: any, action) => {
            const { messageId, error } = action.payload;
            state.feedbackError[messageId] = error;
        },
        setMessageFeedbackStatus: (state: any, action) => {
            const { messageId, isPositive } = action.payload;
            state.feedbackStatus[messageId] = isPositive;
        },
        setInterruptLoading: (state: any, action: PayloadAction<boolean>) => {
            state.interruptLoading = action.payload;
        },
        resetAllChatsStreaming: (state: any) => {
            if (!state.chats) return;
            Object.keys(state.chats).forEach((chatKey: string) => {
                const chat = state.chats[chatKey];
                if (chat) {
                    chat.isStreaming = false;
                }
            });
        },
        setIsLoadingMore: (state: any, action: PayloadAction<boolean>) => {
            state.isLoadingMore = action.payload;
        },
        setCurrentPage: (state: any, action: PayloadAction<number>) => {
            state.currentPage = action.payload;
        },
        setHasMoreChats: (state: any, action: PayloadAction<boolean>) => {
            state.hasMoreChats = action.payload;
        },
        setSearchQuery: (state: any, action: PayloadAction<string>) => {
            const newQuery = action.payload;
            if (newQuery !== state.searchQuery) {
                state.searchQuery = newQuery;
                state.currentPage = 1;
                state.hasMoreChats = true;

            }
        },
        setTotalChats: (state: any, action: PayloadAction<number>) => {
            state.totalChats = action.payload;
        },
        resetPagination: (state: any) => {
            state.currentPage = 1;
            state.hasMoreChats = true;
            state.isLoadingMore = false;
            state.searchQuery = '';
            state.chatHistoryByDate = {
                older: [],
                today: [],
                yesterday: [],
            };
        },

        setMentionSearchLoading: (state: any, action: PayloadAction<boolean>) => {
            state.mentionSearchLoading = action.payload;
        },
        setMentionLoadingMore: (state: any, action: PayloadAction<boolean>) => {
            state.mentionLoadingMore = action.payload;
        },
        setMentionSearchResults: (state: any, action: PayloadAction<{
            searchType: MentionSearchType;
            searchTerm: string;
            items: any[];
            page: number;
            totalCount: number;
            hasMore: boolean;
            append: boolean;
        }>) => {

            const { searchType, searchTerm, items, page, totalCount, hasMore, append } = action.payload;
            const cache = state.mentionSearchCache[searchType];
            if (append) {
                cache.items = [...cache.items, ...items];
            } else {
                cache.items = items;
            }
            cache.searchTerm = searchTerm;
            cache.page = page;
            cache.totalCount = totalCount;
            cache.hasMore = hasMore;
        },
        clearMentionSearchCache: (state: any) => {
            state.mentionSearchCache = DEFAULT_MENTION_SEARCH_CACHE;
        },
        setProfileId: (state: any, action: PayloadAction<number | null>) => {
            state.profileId = action.payload;
        },
        setProfileLoading: (state: any, action: PayloadAction<boolean>) => {
            state.profileLoading = action.payload;
        },
        setProfileError: (state: any, action: PayloadAction<string | null>) => {
            state.profileError = action.payload;
        },
        setIsSharedChat: (state: any, action: PayloadAction<boolean>) => {
            state.isSharedChat = action.payload;
        },
    }
});

export const {
    setChatMessages, setChatMessagesLoading, setChatMessagesError,
    setChatSubquestions, setChatSubquestionsLoading, setChatSubquestionsError,
    setChatLastActiveQuestionsLoading,
    setLastActiveQuestions,
    setChatLastActiveQuestionsError,
    setLastActiveChatId,
    setBigChatboxToggle,
    setChatHistoryList,
    appendChatHistoryList,
    setChatHistoryListError,
    setChatHistoryListLoading,
    setChatMessageById,
    setChatMessageByIdError,
    setChatMessageByIdLoading,
    setPinnedChatsLoading,
    setPinnedChatsError,
    setPinnedChats,
    setChatShareLoading,
    setChatShareError,
    setChatShare,
    removeChatFromHistoryLists,
    setNewChatIdLoading,
    setChatIdForDelete,
    resetChatIdForDelete,
    setProjectId,
    resetProjectId,
    deleteChat,
    updateChatTitle,
    addNewChatToHistory,
    setUserAndBotMessageToChat,
    setQueuedAtForLastMessages,
    setChatToLoading,
    setChatToStreaming,
    updateMessageId,
    setSessionExpiry,
    setSessionId,
    setBotMessageError,
    editLastUserMessage,
    setIsCreatingSession,
    updateThinkingTag,
    updateTransferOrProgressTag,
    addMemorySavedTag,
    updateAnswerTag,
    setMentionSearchLoading,
    setMentionLoadingMore,
    setMentionSearchResults,
    clearMentionSearchCache,
    updateFinalTag,
    updateMessageCancelledTag,
    updateMessageCompleteTag,
    populateChatsFromHistory,
    setFetchedChatWithMessages,
    setIsNewChat,
    setMessageFeedbackLoading,
    setMessageFeedbackError,
    setMessageFeedbackStatus,
    setInterruptLoading,
    resetAllChatsStreaming,
    setIsLoadingMore,
    setCurrentPage,
    setHasMoreChats,
    setSearchQuery,
    setTotalChats,
    resetPagination,
    upsertInterruptOnMessage,
    clearInterruptOnMessage,

    setShowChatbox,
    setShowBigChatbox,
    setShowNormalChatbox,
    setIsChatbotSidebarCollapsed,
    setShowHistoryDropdown,
    setShowSearchInterface,
    setShowSourcesPanel,
    setResearchLinks,
    setContextInfo,
    setChatEntity,
    setThinkingMode,
    setProfileId,
    setProfileLoading,
    setProfileError,
    setIsSharedChat
} = chatMessageSlice.actions;

export default chatMessageSlice.reducer;

